/**
 * Clipboard and "save this text" helpers. OWNER: workstream F.
 *
 * The webview does not always expose `navigator.clipboard` (it needs a secure
 * context, which `tauri://localhost` is but a plain http dev server on a LAN
 * address is not), so every copy falls back to the old execCommand path rather
 * than silently doing nothing.
 */

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea fallback
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Offers `text` to the user as a file. This is a webview download, not an IPC
 * write: the contract has no "write arbitrary file" command, and inventing one
 * client-side would put files outside the case's output root. If the download
 * is blocked we put the content on the clipboard instead and say so.
 */
export async function saveTextAsFile(
  fileName: string,
  text: string,
  mime = "text/csv;charset=utf-8",
): Promise<"downloaded" | "copied" | "failed"> {
  try {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Revoke on the next tick; revoking synchronously cancels the download in
    // WebKitGTK.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return "downloaded";
  } catch {
    return (await copyText(text)) ? "copied" : "failed";
  }
}
